import React, { useEffect, useState, useMemo } from 'react';
import InventoryTable from '../components/sections/InventoryTable';
import AddItemModal from '../components/sections/AddItemModal';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import Select from '../components/ui/Select';
import { Search } from 'lucide-react';
import * as FaIcons from 'react-icons/fa';
import * as MdIcons from 'react-icons/md';
import * as HiIcons from 'react-icons/hi2';
import { getItems, getGroups, searchItem } from '../services/api';
import { toast } from 'react-toastify';
import Pagination from '../components/ui/Pagination';
import { HelmetAsync } from '../utils/HelmetAsync';

function InventoryItems() {

    // const [items, setItems] = useState([]);
    const [groups, setGroups] = useState([]);

    // State for default data
    const [defaultItems, setDefaultItems] = useState([]);
    const [defaultTotalPages, setDefaultTotalPages] = useState(1);

    // State for search results
    const [searchItems, setSearchItems] = useState([]);
    const [searchTotalPages, setSearchTotalPages] = useState(1);

    // Shared state
    const [currentPage, setCurrentPage] = useState(1);
    const [currentLimit, setCurrentLimit] = useState(20);
    const [loading, setLoading] = useState(false);
    const [query, setQuery] = useState("");
    const [field, setField] = useState("");
    const [isSearchActive, setIsSearchActive] = useState(false);

    // Sorting state
    const [sortField, setSortField] = useState("updatedAt");
    const [sortOrder, setSortOrder] = useState("desc");
    const [lowStockSort, setLowStockSort] = useState(false);

    // const [searchTerm, setSearchTerm] = useState('');
    // const [filterGroup, setFilterGroup] = useState(null);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [searchModalOpen, setSearchModalOpen] = useState(false);

    const [searchLoading, setSearchLoading] = useState(false);

    const [itemsPage, setItemsPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    const itemsPerPage = 20;

    const [filters, setFilters] = useState({
        // group: '',
        // quantity: '',
        // name: '',
        page: 1,
        limit: 20,
        sortField: 'updatedAt',
        sortOrder: 'desc',
    });

    const fetchItems = async () => {
        setLoading(true);
        try {
            // const queryString = new URLSearchParams(filters).toString();
            // const itemsResponse = await axios.get(`http://localhost:5000/api/items?${queryString}`);
            // Fetch issues with pagination
            const response = await getItems(currentPage, currentLimit, sortField, sortOrder, lowStockSort);
            
            const sortedItems = response.items
                .map(item => ({
                    ...item,
                    quantity: Number(item.quantity), // Convert quantity to a number
                }))
                .sort((a, b) => {
                    if (sortField === 'quantity') {
                        return sortOrder === 'asc' 
                            ? a.quantity - b.quantity // Ascending order
                            : b.quantity - a.quantity; // Descending order
                    }
                    // If sorting by another field, keep the original order or add additional logic
                    return 0;
                });

            setDefaultItems(sortedItems);
            setDefaultTotalPages(response.totalPages);
            setIsSearchActive(false); // Ensure search is inactive
            // toast.success('Loaded successfully!');

        } catch (err) {
            toast.error(err.message || 'Failed to fetch data');
        } finally {
            setLoading(false);
        }
    };

    // Reset to page 1 when search input is focused or modified
    const handleSearchInputChange = (e) => {
        setQuery(e.target.value);
        if (currentPage !== 1) {
        setCurrentPage(1); // Reset page only if not already 1
        }
    };
    
    const handleSearchInputFocus = () => {
        if (currentPage !== 1) {
        setCurrentPage(1); // Reset page when input is clicked/focused
        }
    };

    const handleSearch = async () => {
        // e.preventDefault();
        setSearchLoading(true);
        // setCurrentPage(1); // Reset to page 1 for new search
        try {
            // const response = await axios.get('/api/items/search', {
            //     params: { query, field },
            // });
            // setCurrentPage(1); // Reset to page 1 for new search
            const response = await searchItem(currentPage, query, field);

            if (response.items.length === 0) {
                toast.warning("No matches found"); // Show warning toast
                resetSearch(); // Reset search if no results
            } else {
                setSearchItems(response.items);
                setSearchTotalPages(response.totalPages);
                setIsSearchActive(true); // Set search as active
            }
            
            // console.log(currentPage);
            // setSearchItems(response.items);
            // setSearchTotalPages(response.totalPages);
            // setIsSearchActive(true); // Set search as active
            // console.log(response.items);
        } catch (err) {
            toast.error(err.message || 'Failed to fetch search results');
            console.error('Error searching items:', err);
        } finally {
            setSearchLoading(false);
        }
    };

    // Reset search and show all items
    const resetSearch = () => {
        setQuery('');
        setField('');
        setSortField('updatedAt');
        setSortOrder('desc');
        setLowStockSort(false);
        setIsSearchActive(false); // Set search as inactive
        setCurrentPage(1);
        // setItems(items); // Reset to show all items
        // fetchItems();
    };

    // Handle page change
    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);

        // if (isSearchActive) {
        //     // Fetch search results for the new page
        //     handleSearch();
        // } else {
        //     // Fetch the full list of items for the new page
        //     fetchItems();
        // }
    };
    
    useEffect(() => {
        if (isSearchActive) {
            handleSearch();
        } else {
            fetchItems(); // Fetch all items if no search is active
        }
        // fetchItems();
    // }, [itemsPage, sortField, sortOrder]); // Fetch data whenever itemsPage changes
    }, [currentPage, currentLimit, sortField, sortOrder, lowStockSort]); // Fetch data whenever itemsPage changes

    const fetchGroups = async () => {
        try {
            const response = await getGroups();
            setGroups(response.groups);

            // toast.success('Groups Loaded successfully!');

        } catch (err) {
            toast.error(err.message || 'Failed to fetch group data');
        }
    };

    useEffect(() => {    
        fetchGroups();
    }, []); // Fetch data whenever itemsPage changes

    // const addItem = (newItem) => {
    // setInventory([...inventory, { ...newItem, id: inventory.length + 1, updatedAt: new Date().toISOString() }]);
    // };

    // const editItem = (editedItem) => {
    // setInventory(inventory.map(item =>
    //     item.id === editedItem.id ? { ...editedItem, updatedAt: new Date().toISOString() } : item
    // ));
    // };

    // const deleteItem = (id) => {
    // setInventory(inventory.filter(item => item.id !== id));
    // };

    // const addGroup = (groupName) => {
    // setItemGroups([...itemGroups, { id: itemGroups.length + 1, name: groupName }]);
    // };

    // const editGroup = (id, newName) => {
    // setItemGroups(itemGroups.map(group => 
    //     group.id === id ? { ...group, name: newName } : group
    // ));
    // };

    // const deleteGroup = (id) => {
    // setItemGroups(itemGroups.filter(group => group.id !== id));
    // };

    // const filteredInventory = useMemo(() => {
    // return inventory.filter(item => 
    //     item.name.toLowerCase().includes(searchTerm.toLowerCase()) &&
    //     // item.altname.toLowerCase().includes(searchTerm.toLowerCase()) &&
    //     (!filterGroup || item.group === filterGroup)
    // );
    // }, [inventory, searchTerm, filterGroup]);

    const toggleSort = () => {
        setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc');
    };

    // Toggle low stock sorting
    const toggleLowStockSort = () => {
        setLowStockSort((prev) => !prev);
        setCurrentPage(1); // Reset to the first page when sorting changes
    };

    const title = `Inventory - BreyERP`;

    return (
    <>
        <HelmetAsync title={title} />
            <div className="flex items-center justify-between mb-3">
                <h1 className="text-xl font-bold">Inventory</h1>
                {/* <Button onClick={() => setSearchModalOpen(true)}>Search Item</Button> */}
                <Button onClick={() => setIsAddModalOpen(true)}><MdIcons.MdAdd className='inline mb-1'/> Add Item</Button>
            </div>

            <div className="bg-white dark:bg-gray-900 shadow-md rounded-lg px-3 py-2 mb-3">
                <div className="flex items-center justify-between flex-col md:flex-row">
                    <div className="text-center">
                        {/* <form onSubmit={handleSearch} className="flex space-x-4"> */}
                        <div className="relative inline-block">
                            <Input
                            type="text"
                            placeholder="Search items..."
                            // value={searchTerm}
                            // onChange={(e) => setSearchTerm(e.target.value)}
                            value={query}
                            // onChange={(e) => setQuery(e.target.value)}
                            onChange={handleSearchInputChange}
                            onFocus={handleSearchInputFocus}
                            className="pl-7 pr-2 py-1 w-full text-sm outline-none"
                            />
                            <Search className="absolute left-1 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                        </div>
                        {/* <select
                            className="w-32 mx-1"
                            value={field}
                            onChange={(e) => setField(e.target.value)}
                        >
                            <option value="">All Fields</option>
                            <option value="name">Name</option>
                            <option value="group">Group</option>
                            <option value="altname">Altname</option>
                        </select> */}
                        <button
                            onClick={handleSearch}
                            // disabled={searchLoading && query}
                            disabled={searchLoading || query === ""}
                            // type='submit'
                            className={`text-xs px-2 py-1 mr-1 bg-blue-500 text-gray-100 rounded-lg transition-colors ${
                                searchLoading || query === "" ? 'bg-gray-500' : 'hover:bg-blue-600'
                            }`}
                        >
                            {searchLoading ?
                            <>
                            <svg aria-hidden="true" role="status" className="inline w-4 h-4 me-1 text-gray-200 animate-spin" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor"/>
                            <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="#1C64F2"/>
                            </svg>
                            Searching
                            </>
                            : 'Search'}
                        </button>
                        {query === "" ? "" :
                        <button
                            onClick={resetSearch}
                            className="text-xs px-2 py-1 text-gray-500 rounded-lg hover:bg-gray-100 transition-colors focus:outline-none active:outline-none"
                            aria-label="Reset"
                            title="Reset"
                        >
                            <HiIcons.HiArrowPathRoundedSquare className='inline mb-0.5'/>
                        </button>
                        }
                        {/* </form> */}
                    </div>
                    {/* <Select
                    value={filterGroup || ''}
                    onChange={(e) => setFilterGroup(e.target.value || null)}
                    className="w-48"
                    >
                    <option value="">All Groups</option>
                    {itemGroups.map((group) => (
                    <option key={group.id} value={group.name}>
                        {group.name}
                    </option>
                    ))}
                    </Select> */}
                    {/* <Select
                    value={sortField}
                    onChange={(e) => {
                    setSortField(e.target.value);
                    setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc');
                    }}
                    className="w-48"
                    >
                        <option value="name">Sort by Name</option>
                        <option value="quantity">Sort by Quantity</option>
                        <option value="price">Sort by Price</option>
                        <option value="recent">Sort by Recent</option>
                    </Select> */}
                    <div>
                        {/* Low Stock Sorting Toggle */}
                        {isSearchActive ? ""
                        :
                            <button
                                onClick={toggleLowStockSort}
                                className={`px-2 py-1 mr-2 text-xs rounded-lg focus:outline-none active:outline-none ${
                                    lowStockSort ? 'bg-red-600 text-red-100' : 'bg-red-100 text-red-600 hover:bg-red-400 hover:text-red-100'
                                }`}
                            >
                                {lowStockSort ?
                                <>Low Stock <MdIcons.MdOutlineCancel className='d-inline mb-0.5' /></> : 'Low Stock'}
                            </button>
                        }
                        <select className='w-36 bg-white' value={sortField} onChange={(e) => setSortField(e.target.value)}>
                            <option value="name">Sort by Name</option>
                            <option value="quantity">Sort by Quantity</option>
                            <option value="group">Sort by Group</option>
                            <option value="updatedAt">Sort by Update</option>
                            <option value="createdAt">Sort by Recent</option>
                        </select>
                        <button 
                            onClick={toggleSort}
                            className="sort-btn focus:outline-none active:outline-none"
                            aria-label={sortOrder === 'desc' ? 'Sort Ascending' : 'Sort Descending'}
                            title={sortOrder === 'desc' ? 'Sort Ascending' : 'Sort Descending'}
                        >
                            {sortOrder === 'desc' ? <FaIcons.FaSortAmountDown /> : <FaIcons.FaSortAmountUp />}
                        </button>
                    </div>
                    {/* <select value={sortOrder} onChange={(e) => setSortOrder(e.target.value)}>
                        <option value="asc">Ascending</option>
                        <option value="desc">Descending</option>
                    </select> */}
                </div>
            </div>
            {loading || searchLoading ? (
                <>
                <div className="flex relative flex-column bg-white shadow-md rounded-lg h-[calc(90vh-108px)] overflow-auto">
                    <table className="w-full">
                    {/* <thead className="bg-gray-50 dark:bg-gray-800"> */}
                    <thead className="bg-gray-200">
                        <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Name</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Quantity</th>
                        {/* <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Selling Price</th> */}
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Group</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Last Updated</th>
                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-600 uppercase tracking-wider">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">

                    <tr className="text-center my-5 py-5 h-96">
                        <td colSpan={6}>
                        <div role="status" className='mt-5 pt-5'>
                            <svg aria-hidden="true" className="inline w-16 h-16 text-gray-200 animate-spin fill-blue-600" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor"/>
                                <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill"/>
                            </svg>
                            <span className="sr-only">Loading...</span>
                        </div>
                        <p className="text-gray-700 text-xl text-center p-6">Loading...</p>
                        </td>
                    </tr>
                    </tbody>
                </table>
            </div>

                </>
            ) : (
            <>
            <InventoryTable
            inventory={(isSearchActive ? searchItems : defaultItems)}
            // onEdit={editItem}
            // onDelete={deleteItem}
            itemGroups={groups}
            refresh={fetchItems}
            />
            <div className="flex justify-between items-center pt-3">
                <div>
                    Show 
                    <select className='border rounded bg-white' value={currentLimit} onChange={(e) => setCurrentLimit(e.target.value)}>
                        <option value="10">10</option>
                        <option value="20">20</option>
                        <option value="50">50</option>
                        <option value="100">100</option>
                    </select>
                    per page
                </div>
            <Pagination
                itemsPage={currentPage}
                totalPages={isSearchActive ? searchTotalPages : defaultTotalPages}
                handlePageChange={handlePageChange}
            />
            </div>
            </>
            )}

            {/* <nav>
                <div className="row mx-0 align-items-center">
                    
                    <div className="col-lg-12 text-left px-0">
                        <ul className="pagination">
                            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                <button
                                    className='page-link'
                                    onClick={() => handlePageChange(prev => Math.max(prev - 1, 1))}
                                    disabled={currentPage === 1}
                                >
                                    <MdIcons.MdArrowLeft/>
                                </button>
                            </li>

                            Always show the first page
                            <li className={`page-item ${currentPage === 1 ? 'active' : ''}`}>
                                <button
                                    className="page-link"
                                    onClick={() => handlePageChange(1)}
                                >
                                    1
                                </button>
                            </li>

                            Show ellipsis if current page is far from the start
                            {currentPage > 4 && (
                                <li className="page-item disabled">
                                    <span className="page-link">...</span>
                                </li>
                            )}

                            Show pages around the current page
                            {[...Array(isSearchActive ? searchTotalPages : defaultTotalPages)].map((_, index) => {
                                const pageNumber = index + 1;
                                Only display pages within a certain range of the current page
                                if (pageNumber > 1 && pageNumber < (isSearchActive ? searchTotalPages : defaultTotalPages) && Math.abs(pageNumber - currentPage) <= 1) {
                                    return (
                                        <li
                                            key={index}
                                            className={`page-item ${currentPage === pageNumber ? 'active' : ''}`}
                                        >
                                            <button
                                                className="page-link"
                                                onClick={() => handlePageChange(pageNumber)}
                                            >
                                                {pageNumber}
                                            </button>
                                        </li>
                                    );
                                }
                                return null;
                            })}

                            Show ellipsis if current page is far from the end
                            {currentPage < isSearchActive ? searchTotalPages : defaultTotalPages - 3 && (
                                <li className="page-item disabled">
                                    <span className="page-link">...</span>
                                </li>
                            )}

                            Always show the last page
                            {isSearchActive ? searchTotalPages : defaultTotalPages > 1 && (
                                <li className={`page-item ${currentPage === isSearchActive ? searchTotalPages : defaultTotalPages ? 'active' : ''}`}>
                                    <button
                                        className="page-link"
                                        onClick={() => handlePageChange(isSearchActive ? searchTotalPages : defaultTotalPages)}
                                    >
                                        {isSearchActive ? searchTotalPages : defaultTotalPages}
                                    </button>
                                </li>
                            )}

                            <li className={`page-item ${currentPage === isSearchActive ? searchTotalPages : defaultTotalPages ? 'disabled' : ''}`}>
                                <button
                                    className='page-link'
                                    onClick={() => handlePageChange(prev => Math.min(prev + 1, isSearchActive ? searchTotalPages : defaultTotalPages))}
                                    disabled={currentPage === isSearchActive ? searchTotalPages : defaultTotalPages}
                                >
                                    <MdIcons.MdArrowRight/>
                                </button>
                            </li>
                        </ul>
                    </div>
                </div>
            </nav> */}

            {/* <div className="mt-4 flex justify-between items-center">
                <div>
                    Showing {Math.min((currentPage - 1) * itemsPerPage + 1, sortedInventory.length)} to {Math.min(currentPage * itemsPerPage, sortedInventory.length)} of {sortedInventory.length} items
                </div>
                <div className="flex space-x-2">
                    <Button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    variant="outline"
                    >
                    Previous
                    </Button>
                    <Button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, pageCount))}
                    disabled={currentPage === pageCount}
                    variant="outline"
                    >
                    Next
                    </Button>
                </div>
            </div> */}
            {/* <GroupManagement
            groups={itemGroups}
            onAddGroup={addGroup}
            onEditGroup={editGroup}
            onDeleteGroup={deleteGroup}
            /> */}

            {isAddModalOpen && (
            <AddItemModal
            onClose={() => {
                setIsAddModalOpen(false)
                // fetchItems();
            }}
            // onAdd={addItem}
            onAdd={() => {
                fetchItems();
            }}
            itemGroups={groups}
            />
            )}
        </>
    );
}

export default InventoryItems;